/**
 * Koordinatsystemer for kart og vektorlag. Punktskyen transformeres ALDRI – alt annet føres inn i prosjektets
 * CRS (f.eks. EPSG:25832) og deretter til lokal visningsramme (− origin), samme som punktskyen.
 *
 * Definisjonene er eksplisitte (ingen nettoppslag). ETRS89 og WGS84 behandles som like (avvik < 1 m i Norge,
 * og DJI RTK/CPOS leverer ETRS89) – dokumentert i README.
 */

import proj4 from 'proj4';
import type {SourceCrs} from './sourceCrs';

const DEFS: Record<string, string> = {
  'EPSG:4326': '+proj=longlat +datum=WGS84 +no_defs',
  'EPSG:4258': '+proj=longlat +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +no_defs',
  'EPSG:25832': '+proj=utm +zone=32 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
  'EPSG:25833': '+proj=utm +zone=33 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
  'EPSG:25835': '+proj=utm +zone=35 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
  'EPSG:32632': '+proj=utm +zone=32 +datum=WGS84 +units=m +no_defs',
  'EPSG:32633': '+proj=utm +zone=33 +datum=WGS84 +units=m +no_defs',
  'EPSG:3857': '+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +no_defs',
};
for (const [k, v] of Object.entries(DEFS)) proj4.defs(k, v);

export const SUPPORTED_CRS = Object.keys(DEFS);

/** Normaliser CRS-angivelser: "urn:ogc:def:crs:EPSG::25832", "EPSG:25832", "OGC:CRS84", "CRS84" → "EPSG:xxxx". */
export function normalizeCrs(s?: string | null): string | null {
  if (!s) return null;
  const t = s.trim();
  if (/CRS84$/i.test(t)) return 'EPSG:4326';
  const m = t.match(/EPSG:{1,2}(\d+)$/i) ?? t.match(/^(\d{4,5})$/);
  return m ? `EPSG:${m[1]}` : null;
}

export function isSupported(crs: string | null): boolean {
  return !!crs && crs in DEFS;
}

export type Transform = (x: number, y: number) => [number, number];

export function transformer(from: string, to: string): Transform {
  if (from === to) return (x, y) => [x, y];
  if (!isSupported(from) || !isSupported(to)) throw new Error(`CRS ikke støttet: ${from} → ${to}`);
  const p = proj4(from, to);
  return (x, y) => p.forward([x, y]) as [number, number];
}

export interface GeoJsonCrsInfo {
  crs: string;
  /** hvordan CRS ble bestemt – vises i laginfo */
  source: 'crs-member' | 'rfc7946-default';
  warning?: string;
  sourceMetadata?: SourceCrs;
}

/**
 * CRS for et GeoJSON-dokument:
 * - eksplisitt `crs`-medlem (eldre GeoJSON, f.eks. fra GDAL/QGIS) brukes
 * - ellers RFC 7946: CRS84 (lon/lat)
 * - hvis koordinatene åpenbart ikke er lon/lat (|x| > 180) brukes prosjektets CRS, med advarsel
 */
export function detectGeoJsonCrs(doc: { crs?: { properties?: { name?: string } }; source_crs?:SourceCrs }, sample: number[] | null, projectCrs: string): GeoJsonCrsInfo {
  const named = normalizeCrs(doc?.crs?.properties?.name);
  if (doc?.crs && !named) throw new Error('Ukjent eller ugyldig CRS i GeoJSON. Laget kan ikke plasseres sikkert.');
  if (named) return { crs: named, source: 'crs-member', sourceMetadata:doc.source_crs };
  if (sample && (Math.abs(sample[0]) > 180 || Math.abs(sample[1]) > 90)) {
    throw new Error(`GeoJSON mangler CRS og har projiserte koordinater. Oppgi eksplisitt CRS før visning i ${projectCrs}.`);
  }
  return { crs: 'EPSG:4326', source: 'rfc7946-default' };
}
